import {
  mempoolSpaceResolver,
  verifyReceipt,
  type FileVouchReceipt,
  type VerificationResult,
} from "@filevouch/verifier-core";

// Wire up DOM in one pass so TS narrows once. If any of these don't exist
// it's a hard build error in the bundle — fail noisily.
const $ = <T extends HTMLElement>(id: string): T => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`missing element #${id}`);
  return el as T;
};

const fileInput = $<HTMLInputElement>("file");
const receiptInput = $<HTMLInputElement>("receipt");
const fileDrop = $<HTMLLabelElement>("fileDrop");
const receiptDrop = $<HTMLLabelElement>("receiptDrop");
const fileLabel = $<HTMLElement>("fileLabel");
const receiptLabel = $<HTMLElement>("receiptLabel");
const urlInput = $<HTMLInputElement>("url");
const loadUrlBtn = $<HTMLButtonElement>("loadUrl");
const onlineCheck = $<HTMLInputElement>("onlineCheck");
const verifyBtn = $<HTMLButtonElement>("verify");
const verdict = $<HTMLElement>("verdict");
const errBox = $<HTMLElement>("err");
const checksBox = $<HTMLElement>("checks");

let fileBytes: Uint8Array | null = null;
let receipt: FileVouchReceipt | null = null;

function refreshButton() {
  verifyBtn.disabled = !(fileBytes && receipt);
}

function showError(msg: string) {
  errBox.textContent = msg;
  errBox.hidden = false;
}
function clearError() {
  errBox.hidden = true;
  errBox.textContent = "";
}

async function pickFile(file: File) {
  // Clear before the await so Verify can't fire against stale bytes
  // while a new file is still streaming in.
  fileBytes = null;
  refreshButton();
  fileBytes = new Uint8Array(await file.arrayBuffer());
  fileLabel.classList.add("picked");
  fileLabel.textContent = `${file.name} (${file.size.toLocaleString()} bytes)`;
  refreshButton();
}

async function pickReceiptFromFile(file: File) {
  receipt = null;
  refreshButton();
  const text = await file.text();
  parseReceipt(text, file.name);
}

function parseReceipt(text: string, label: string) {
  try {
    const parsed = JSON.parse(text);
    // Trust the schema validation in verifyReceipt — we just need a shape
    // that has subject.hash to render before the click. If it's malformed,
    // verifyReceipt's hash check will surface the failure with structure.
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      typeof parsed.subject?.hash !== "string"
    ) {
      throw new Error("not a FileVouch receipt (missing subject.hash)");
    }
    receipt = parsed as FileVouchReceipt;
    receiptLabel.classList.add("picked");
    receiptLabel.textContent = `${label} — receipt for hash ${shortHash(receipt.subject.hash)}`;
    clearError();
    refreshButton();
  } catch (e) {
    showError(`Couldn't parse receipt: ${(e as Error).message}`);
    receipt = null;
    refreshButton();
  }
}

function shortHash(hex: string): string {
  return hex.length > 16 ? `${hex.slice(0, 8)}…${hex.slice(-8)}` : hex;
}

// Drop-zone wiring. Click on the label opens the picker (default browser
// behavior); we just add drag-over styling + drop fallback.
function wireDrop(zone: HTMLElement, onFile: (f: File) => void) {
  zone.addEventListener("dragover", (e) => {
    e.preventDefault();
    zone.classList.add("over");
  });
  zone.addEventListener("dragleave", () => zone.classList.remove("over"));
  zone.addEventListener("drop", (e) => {
    e.preventDefault();
    zone.classList.remove("over");
    const f = e.dataTransfer?.files?.[0];
    if (f) onFile(f);
  });
}
wireDrop(fileDrop, (f) => void pickFile(f));
wireDrop(receiptDrop, (f) => void pickReceiptFromFile(f));

fileInput.addEventListener("change", () => {
  const f = fileInput.files?.[0];
  if (f) void pickFile(f);
});
receiptInput.addEventListener("change", () => {
  const f = receiptInput.files?.[0];
  if (f) void pickReceiptFromFile(f);
});

// Reject anything that isn't an https URL. Without this, a crafted
// `?receipt=data:application/json,...` deep-link could inject a fake
// receipt bypassing the "from receipts.filevouch.com" trust signal —
// and `javascript:` / `file:` URLs are obviously a no-go.
function isAllowedReceiptUrl(raw: string): boolean {
  try {
    return new URL(raw).protocol === "https:";
  } catch {
    return false;
  }
}

async function loadReceiptFromUrl(url: string): Promise<void> {
  clearError();
  if (!isAllowedReceiptUrl(url)) {
    showError("Receipt URL must use https://");
    return;
  }
  loadUrlBtn.disabled = true;
  receipt = null;
  refreshButton();
  try {
    const res = await fetch(url, {
      headers: { accept: "application/json" },
      signal: AbortSignal.timeout(15_000),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const text = await res.text();
    parseReceipt(text, url.split("/").pop() ?? url);
  } catch (e) {
    showError(`Couldn't fetch receipt: ${(e as Error).message}`);
  } finally {
    loadUrlBtn.disabled = false;
  }
}

loadUrlBtn.addEventListener("click", () => {
  void loadReceiptFromUrl(urlInput.value.trim());
});

verifyBtn.addEventListener("click", async () => {
  if (!fileBytes || !receipt) return;
  verifyBtn.disabled = true;
  verdict.className = "verdict idle";
  verdict.textContent = "Verifying…";
  checksBox.innerHTML = "";
  clearError();

  try {
    const result = await verifyReceipt(receipt, fileBytes, {
      ...(onlineCheck.checked
        ? { resolveBlock: mempoolSpaceResolver }
        : {}),
    });
    render(result);
  } catch (e) {
    showError(`Verifier threw: ${(e as Error).message}`);
    verdict.className = "verdict fail";
    verdict.textContent = "Verifier crashed — see details above.";
  } finally {
    verifyBtn.disabled = false;
  }
});

function render(r: VerificationResult) {
  verdict.className = r.ok ? "verdict ok" : "verdict fail";
  verdict.textContent = r.ok
    ? "Receipt verifies. The file's hash, Merkle path, TSA signatures, and Bitcoin anchor all check out."
    : "One or more checks failed. See the breakdown below.";

  const rows: Array<{
    title: string;
    desc: string;
    state: "ok" | "fail" | "skip";
    detail?: string;
  }> = [
    {
      title: "File hash",
      desc: "SHA-256 of the file matches the hash in receipt.subject.",
      state: r.hash.ok ? "ok" : "fail",
      ...(r.hash.ok ? {} : { detail: r.hash.reason }),
    },
  ];
  if (r.merkle) {
    rows.push({
      title: "Merkle inclusion",
      desc: "The receipt's audit path reconstructs the declared root.",
      state: r.merkle.ok ? "ok" : "fail",
      ...(r.merkle.ok ? {} : { detail: r.merkle.reason }),
    });
  } else {
    rows.push({
      title: "Merkle inclusion",
      desc: "Not present — single-stamp receipt with no batch.",
      state: "skip",
    });
  }
  if (r.tsa) {
    rows.push({
      title: "TSA signatures",
      desc: r.tsa.ok
        ? r.tsa.note ?? "All TSA entries verified."
        : "TSA verification.",
      state: r.tsa.ok ? "ok" : "fail",
      ...(r.tsa.ok ? {} : { detail: r.tsa.reason }),
    });
  } else {
    rows.push({
      title: "TSA signatures",
      desc: "Receipt has no TSA evidence (anon free tier).",
      state: "skip",
    });
  }
  if (r.bitcoin) {
    rows.push({
      title: "Bitcoin anchor",
      desc: r.bitcoin.ok
        ? r.bitcoin.note ?? "OTS proof anchors on Bitcoin."
        : "Bitcoin anchor.",
      state: r.bitcoin.ok ? "ok" : "fail",
      ...(r.bitcoin.ok ? {} : { detail: r.bitcoin.reason }),
    });
  } else {
    rows.push({
      title: "Bitcoin anchor",
      desc: "Status is pending — receipt not yet anchored on Bitcoin.",
      state: "skip",
    });
  }

  checksBox.innerHTML = "";
  for (const row of rows) {
    const el = document.createElement("div");
    el.className = `check ${row.state}`;
    const icon = row.state === "ok" ? "✓" : row.state === "fail" ? "✗" : "—";
    const detail = row.detail
      ? `<pre>${escapeHtml(row.detail)}</pre>`
      : "";
    el.innerHTML = `
      <div class="icon">${icon}</div>
      <div>
        <h4>${escapeHtml(row.title)}</h4>
        <p>${escapeHtml(row.desc)}</p>
        ${detail}
      </div>
    `;
    checksBox.appendChild(el);
  }
}

function escapeHtml(s: string): string {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

// Auto-load if URL has ?receipt=… query param. Ergonomic deep-link path
// for "click here to verify" flows from third-party sites. Goes through
// the same scheme guard as the manual Load button.
const params = new URLSearchParams(location.search);
const receiptParam = params.get("receipt");
if (receiptParam) {
  urlInput.value = receiptParam;
  void loadReceiptFromUrl(receiptParam);
}
